import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { useLocation } from 'react-router-dom';
import { Icon24Copy, Icon24LogoDiscordLarge, Icon24Upload } from '@figma/fpl-icons';
import { ColorTheme } from '@figma/fpl-themes';
import { H1, TextLgRg, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { Stack } from '@/UI/Stack/Stack';
import { WeaveThemeProvider } from '@/UI/WeaveThemeProvider/WeaveThemeProvider';
import { WeavyLogoFilled } from '@/UI/Icons/WeavyLogoFilled';
import { MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES } from '@/consts/analytics.consts';
import { EXTERNAL_LINKS } from '@/consts/externalLinks.consts';
import { getMobileHandoffUrl, getMobileSignupHandoffRedirectPath } from '@/consts/mobileHandoff.consts';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import handoffBackgroundVideo from './assets/weave_mobile_asset.mp4';

type HandoffStatus = 'idle' | 'shared' | 'copied' | 'error';
type CopyMethod = 'primary' | 'secondary' | 'share_fallback';
const FPL_ICON_COLOR_VAR = '--fpl-icon-color';

const getIconColorStyle = (iconColor: string): CSSProperties =>
  ({
    color: iconColor,
    [FPL_ICON_COLOR_VAR]: iconColor,
  }) as CSSProperties;

const actionButtonLabelStyle: CSSProperties = {
  fontSize: 'var(--text-body-large-font-size, 0.8125rem)',
  fontWeight: 'var(--text-body-large-font-weight, 450)',
  lineHeight: 'var(--text-body-large-line-height, 1.375rem)',
};

const isShareCanceled = (error: unknown): boolean => {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
};

function ActionButtonLabel({ children }: { children: ReactNode }) {
  return <span style={actionButtonLabelStyle}>{children}</span>;
}

export function MobileSignupHandoffPage() {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const location = useLocation();
  const [handoffStatus, setHandoffStatus] = useState<HandoffStatus>('idle');
  const [isSharePending, setIsSharePending] = useState(false);
  const hasTrackedViewRef = useRef(false);
  const desktopLink = getMobileHandoffUrl(
    window.location.origin,
    getMobileSignupHandoffRedirectPath(location.pathname, location.search, location.hash),
  );
  const canShare = typeof navigator.share === 'function';

  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.PAGE_VIEWED);
  }, [posthog]);

  const copyDesktopLink = useCallback(
    async (method: CopyMethod) => {
      try {
        if (!navigator.clipboard?.writeText) {
          throw new Error('Clipboard API unavailable');
        }
        await navigator.clipboard.writeText(desktopLink);
        setHandoffStatus('copied');
        posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.LINK_COPIED, { method });
      } catch {
        setHandoffStatus('error');
        posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.LINK_COPY_FAILED, { method });
      }
    },
    [desktopLink, posthog],
  );

  const sendLinkToDesktop = useCallback(async () => {
    if (!canShare) {
      await copyDesktopLink('primary');
      return;
    }

    posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.SHARE_STARTED);
    try {
      await navigator.share({
        title: t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.SHARE_TITLE),
        url: desktopLink,
      });
      setHandoffStatus('shared');
      posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.SHARE_COMPLETED);
    } catch (error) {
      if (isShareCanceled(error)) {
        posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.SHARE_CANCELED);
        return;
      }
      posthog.capture(MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES.SHARE_FAILED);
      await copyDesktopLink('share_fallback');
    }
  }, [canShare, copyDesktopLink, desktopLink, posthog, t]);

  const handleDiscordClick = useCallback(() => {
    window.open(EXTERNAL_LINKS.discordInvite);
  }, []);

  const handleSendLinkToDesktop = useCallback(() => {
    if (isSharePending) return;
    setIsSharePending(true);
    setHandoffStatus('idle');
    void sendLinkToDesktop().finally(() => {
      setIsSharePending(false);
    });
  }, [isSharePending, sendLinkToDesktop]);

  const handleCopyDesktopLink = useCallback(() => {
    setHandoffStatus('idle');
    void copyDesktopLink('secondary');
  }, [copyDesktopLink]);

  const statusMessage =
    handoffStatus === 'copied'
      ? t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.COPY_SUCCESS)
      : handoffStatus === 'shared'
        ? t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.SHARE_SUCCESS)
        : handoffStatus === 'error'
          ? t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.COPY_ERROR)
          : null;

  return (
    <WeaveThemeProvider>
      <ColorTheme brand="weave" mode="light">
        <Stack
          direction="column"
          sx={{
            backgroundColor: color.White100,
            boxSizing: 'border-box',
            height: '100vh',
            maxHeight: '100vh',
            maxWidth: '100vw',
            overflow: 'hidden',
            position: 'relative',
            width: '100vw',
            '@supports (height: 100dvh)': {
              height: '100dvh',
              maxHeight: '100dvh',
            },
          }}
        >
          <video
            src={handoffBackgroundVideo}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden
            style={{
              height: '100%',
              inset: 0,
              objectFit: 'cover',
              pointerEvents: 'none',
              position: 'absolute',
              width: '100%',
            }}
          />
          <Stack sx={{ p: 3, position: 'relative' }}>
            <WeavyLogoFilled
              title="Figma Weave Logo"
              style={{ width: 35, height: 35, color: color.Black100, ...getIconColorStyle(color.Black100) }}
            />
          </Stack>
          <Stack
            direction="column"
            justifyContent="space-between"
            sx={{ flex: 1, gap: 4, pb: 7, position: 'relative', width: '100%' }}
          >
            <Stack sx={{ flex: 1 }} />
            <Stack direction="column" gap={3} sx={{ px: 4 }}>
              <Stack direction="column" gap={1.5}>
                <H1
                  component="h1"
                  style={{ fontSize: '2rem', fontWeight: 600, lineHeight: '38px' }}
                  sx={{ color: color.Black92, m: 0 }}
                >
                  {t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.POST_SIGNUP_TITLE)}
                </H1>
                <TextLgRg
                  component="p"
                  color={color.Black92}
                  style={{ fontSize: '1rem', lineHeight: '24px' }}
                  sx={{ m: 0 }}
                >
                  {t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.POST_SIGNUP_SUBTITLE)}
                </TextLgRg>
              </Stack>
              <Stack direction="column" gap={1.5} sx={{ '& > button': { minHeight: '44px' } }}>
                <ButtonContained
                  fullWidth
                  isFigmaTheme
                  mode="filled-dark"
                  size="large"
                  startIcon={
                    canShare ? (
                      <Icon24Upload aria-hidden style={getIconColorStyle(color.White100)} />
                    ) : (
                      <Icon24Copy aria-hidden style={getIconColorStyle(color.White100)} />
                    )
                  }
                  onClick={handleSendLinkToDesktop}
                  disabled={isSharePending}
                >
                  <ActionButtonLabel>
                    {t(
                      canShare
                        ? I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.SHARE_LINK_BUTTON
                        : I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.COPY_LINK_BUTTON,
                    )}
                  </ActionButtonLabel>
                </ButtonContained>
                {canShare && (
                  <ButtonContained
                    fullWidth
                    isFigmaTheme
                    mode="outlined-light"
                    size="large"
                    startIcon={<Icon24Copy aria-hidden />}
                    onClick={handleCopyDesktopLink}
                  >
                    <ActionButtonLabel>{t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.COPY_LINK_BUTTON)}</ActionButtonLabel>
                  </ButtonContained>
                )}
                {statusMessage && (
                  <TextSmRg role="status" aria-live="polite" color={color.Black64_T}>
                    {statusMessage}
                  </TextSmRg>
                )}
              </Stack>
            </Stack>
            <Stack sx={{ alignSelf: 'center' }}>
              <ButtonContained
                fullWidth
                isFigmaTheme
                mode="text"
                size="large"
                startIcon={<Icon24LogoDiscordLarge aria-hidden />}
                onClick={handleDiscordClick}
                sx={{ border: 'none', color: color.Black92_T, minHeight: 42, px: 2 }}
              >
                <ActionButtonLabel>{t(I18N_KEYS.MOBILE_SIGNUP_HANDOFF_PAGE.DISCORD_BUTTON)}</ActionButtonLabel>
              </ButtonContained>
            </Stack>
          </Stack>
        </Stack>
      </ColorTheme>
    </WeaveThemeProvider>
  );
}
